import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
  useEffect,
} from 'react'
import { App } from 'antd'
import { addVideographer, fetchVideographers } from '../api'

const MAX_VIDEOGRAPHERS = 5

type TeamType = 'home' | 'visitor' | null

interface VideographerState {
  step: 'team-selection' | 'email-form'
  selectedTeam: TeamType
  email: string
  isLoading: boolean
  error: string | null
  homeCount: number
  visitorCount: number
  homeDisabled: boolean
  visitorDisabled: boolean
}

type VideographerAction =
  | { type: 'SET_STEP'; step: 'team-selection' | 'email-form' }
  | { type: 'SELECT_TEAM'; team: TeamType }
  | { type: 'SET_EMAIL'; email: string }
  | { type: 'SET_LOADING'; value: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | {
      type: 'SET_COUNTS'
      homeCount: number
      visitorCount: number
    }
  | { type: 'RESET' }

const initialState: VideographerState = {
  step: 'team-selection',
  selectedTeam: null,
  email: '',
  isLoading: false,
  error: null,
  homeCount: 0,
  visitorCount: 0,
  homeDisabled: false,
  visitorDisabled: false,
}

function videographerReducer(
  state: VideographerState,
  action: VideographerAction
): VideographerState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.step, error: null }

    case 'SELECT_TEAM':
      return { ...state, selectedTeam: action.team }

    case 'SET_EMAIL':
      return { ...state, email: action.email, error: null }

    case 'SET_LOADING':
      return { ...state, isLoading: action.value }

    case 'SET_ERROR':
      return { ...state, error: action.error }

    case 'SET_COUNTS':
      return {
        ...state,
        homeCount: action.homeCount,
        visitorCount: action.visitorCount,
        homeDisabled: action.homeCount >= MAX_VIDEOGRAPHERS,
        visitorDisabled: action.visitorCount >= MAX_VIDEOGRAPHERS,
      }

    case 'RESET':
      return { ...initialState }

    default:
      return state
  }
}

interface VideographerContextValue {
  state: VideographerState
  dispatch: React.Dispatch<VideographerAction>
  gameId: string
  homeRosterId: string | null
  visitorRosterId: string | null
  homeTeamName: string
  visitorTeamName: string
  homeTeamImage?: string | number | null
  visitorTeamImage?: string | number | null
  onSubmit: () => Promise<void>
  onCancel: () => void
  onBack: () => void
  onSelectTeam: (team: 'home' | 'visitor') => void
}

const VideographerContext = createContext<VideographerContextValue | null>(null)

export const useVideographerContext = () => {
  const context = useContext(VideographerContext)
  if (!context) {
    throw new Error(
      'useVideographerContext must be used within VideographerProvider'
    )
  }
  return context
}

interface VideographerProviderProps {
  children: ReactNode
  gameId: string
  homeRosterId: string | null
  visitorRosterId: string | null
  homeTeamName: string
  visitorTeamName: string
  onClose: () => void
  onSuccess: () => void
  homeTeamImage?: string | number | null
  visitorTeamImage?: string | number | null
  preSelectedTeam?: 'home' | 'visitor'
}

export const VideographerProvider: React.FC<VideographerProviderProps> = ({
  children,
  gameId,
  homeRosterId,
  visitorRosterId,
  homeTeamName,
  visitorTeamName,
  onClose,
  onSuccess,
  homeTeamImage,
  visitorTeamImage,
  preSelectedTeam,
}) => {
  const preSelectedInitialState: VideographerState = preSelectedTeam
    ? {
        ...initialState,
        step: 'email-form',
        selectedTeam: preSelectedTeam,
      }
    : initialState

  const [state, dispatch] = useReducer(
    videographerReducer,
    preSelectedInitialState
  )
  const { message } = App.useApp()

  // Load initial counts
  useEffect(() => {
    const loadCounts = async () => {
      try {
        const [homeData, visitorData] = await Promise.all([
          homeRosterId ? fetchVideographers(gameId, homeRosterId) : [],
          visitorRosterId ? fetchVideographers(gameId, visitorRosterId) : [],
        ])
        dispatch({
          type: 'SET_COUNTS',
          homeCount: homeData.length,
          visitorCount: visitorData.length,
        })
      } catch {
        // Silently fail - counts will be 0
      }
    }
    loadCounts()
  }, [gameId, homeRosterId, visitorRosterId])

  const onSelectTeam = useCallback((team: 'home' | 'visitor') => {
    dispatch({ type: 'SELECT_TEAM', team })
    dispatch({ type: 'SET_STEP', step: 'email-form' })
  }, [])

  const onBack = useCallback(() => {
    if (preSelectedTeam) {
      dispatch({ type: 'RESET' })
      onClose()
      return
    }
    dispatch({ type: 'SET_STEP', step: 'team-selection' })
    dispatch({ type: 'SET_EMAIL', email: '' })
    dispatch({ type: 'SELECT_TEAM', team: null })
  }, [preSelectedTeam, onClose])

  const onCancel = useCallback(() => {
    dispatch({ type: 'RESET' })
    onClose()
  }, [onClose])

  const onSubmit = useCallback(async () => {
    // Validate email
    const emailTrimmed = state.email.trim()
    if (!emailTrimmed) {
      dispatch({ type: 'SET_ERROR', error: 'Email is required' })
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(emailTrimmed)) {
      dispatch({ type: 'SET_ERROR', error: 'Please enter a valid email' })
      return
    }

    const rosterId =
      state.selectedTeam === 'home' ? homeRosterId : visitorRosterId
    if (!rosterId) {
      dispatch({ type: 'SET_ERROR', error: 'Invalid team selection' })
      return
    }

    dispatch({ type: 'SET_LOADING', value: true })
    dispatch({ type: 'SET_ERROR', error: null })

    try {
      await addVideographer(gameId, rosterId, { email: emailTrimmed })
      message.success('Videographer invitation sent successfully')
      dispatch({ type: 'RESET' })
      onSuccess()
      onClose()
    } catch (e) {
      const err = e as {
        message?: string
        invalid_fields?: Record<string, string[]>
      }
      if (err.invalid_fields?.email) {
        dispatch({
          type: 'SET_ERROR',
          error: err.invalid_fields.email.join(', '),
        })
      } else {
        dispatch({
          type: 'SET_ERROR',
          error: err.message || 'Failed to send invitation',
        })
      }
    } finally {
      dispatch({ type: 'SET_LOADING', value: false })
    }
  }, [
    state.email,
    state.selectedTeam,
    gameId,
    homeRosterId,
    visitorRosterId,
    message,
    onSuccess,
    onClose,
  ])

  const value: VideographerContextValue = {
    state,
    dispatch,
    gameId,
    homeRosterId,
    visitorRosterId,
    homeTeamName,
    visitorTeamName,
    homeTeamImage,
    visitorTeamImage,
    onSubmit,
    onCancel,
    onBack,
    onSelectTeam,
  }

  return (
    <VideographerContext.Provider value={value}>
      {children}
    </VideographerContext.Provider>
  )
}

export default VideographerContext
